import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@clerk/clerk-react';
import {
  fetchAccreditationBodies,
  fetchFirmTeam,
  fetchMyFirm,
  removeFirmMember,
  setFirmMemberRole,
  setupMyFirm,
  unblockFirmEmail,
  updateMyFirm,
} from '../api/verifierFirmApi';

// The active verifier firm (verifier-firm-foundation phase 2). Keyed on the org so a workspace
// switch never shows the previous firm.
export const myFirmKey = (orgId) => ['verifier-firm', orgId];
export const firmTeamKey = (orgId) => ['verifier-firm-team', orgId];

export function useMyFirm() {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  return useQuery({
    queryKey: myFirmKey(orgId),
    queryFn: fetchMyFirm,
    enabled: Boolean(orgId) && organization?.publicMetadata?.type === 'verifier',
  });
}

function useFirmMutation(fn) {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // mutateAsync must wait for the refreshed firm before setup navigates: the
    // destination's setup gate otherwise sees the old setupComplete=false.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: myFirmKey(organization?.id) }),
        queryClient.invalidateQueries({ queryKey: firmTeamKey(organization?.id) }),
      ]),
  });
}

export const useSetupMyFirm = () => useFirmMutation(setupMyFirm);
export const useUpdateMyFirm = () => useFirmMutation(updateMyFirm);
export const useRemoveFirmMember = () => useFirmMutation(removeFirmMember);
export const useSetFirmMemberRole = () =>
  useFirmMutation(({ clerkUserId, role }) => setFirmMemberRole(clerkUserId, role));
export const useUnblockFirmEmail = () => useFirmMutation(unblockFirmEmail);

/** The Team page's members: joined, last active and staffing (workspace-ux phase 4). */
export function useFirmTeam() {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  return useQuery({
    queryKey: firmTeamKey(orgId),
    queryFn: fetchFirmTeam,
    enabled: Boolean(orgId) && organization?.publicMetadata?.type === 'verifier',
  });
}

/** The EU national accreditation bodies (workspace-ux W10). Static reference data. */
export function useAccreditationBodies() {
  return useQuery({
    queryKey: ['verifier-accreditation-bodies'],
    queryFn: fetchAccreditationBodies,
    staleTime: Infinity,
  });
}
